import { useState, useEffect, useRef, useCallback } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Gift, Plus, X, Search, Printer, Check, Clock, Ban, Trash2, ChevronDown, ChevronUp, Eye, Send as SendIcon, Camera, QrCode, Copy, ExternalLink, Sparkles, Loader2, Mail, Settings, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import QrScannerModal from '../../components/QrScannerModal';

const OCCASIONS = [
  { value: '', label: 'Sans occasion' },
  { value: 'noel', label: 'Noel' },
  { value: 'saint-valentin', label: 'Saint-Valentin' },
  { value: 'anniversaire', label: 'Anniversaire' },
  { value: 'fete-des-meres', label: 'Fete des meres' },
  { value: 'fete-des-peres', label: 'Fete des peres' },
  { value: 'mariage', label: 'Mariage' },
  { value: 'paques', label: 'Paques' },
  { value: 'remerciement', label: 'Remerciement' },
];

const OCCASION_COLORS = {
  'noel': 'bg-red-800/20 text-red-300 border-red-800/30',
  'saint-valentin': 'bg-pink-800/20 text-pink-300 border-pink-800/30',
  'anniversaire': 'bg-purple-800/20 text-yellow-300 border-purple-800/30',
  'fete-des-meres': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'fete-des-peres': 'bg-blue-800/20 text-blue-300 border-blue-800/30',
  'mariage': 'bg-amber-800/20 text-amber-200 border-amber-800/30',
  'paques': 'bg-green-800/20 text-green-300 border-green-800/30',
  'remerciement': 'bg-gray-700/20 text-gray-300 border-gray-700/30',
};

const STATUT_COLORS = {
  CREE: { bg: 'bg-blue-400/15', text: 'text-blue-400', label: 'Créé' },
  EN_ATTENTE: { bg: 'bg-orange-400/15', text: 'text-orange-400', label: 'En attente' },
  UTILISE: { bg: 'bg-green-400/15', text: 'text-green-400', label: 'Utilisé' },
  EXPIRE: { bg: 'bg-red-400/15', text: 'text-red-400', label: 'Expiré' },
};

const StatutBadge = ({ statut }) => {
  const s = STATUT_COLORS[statut] || STATUT_COLORS.CREE;
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${s.bg} ${s.text}`} data-testid={`statut-${statut}`}>{s.label}</span>;
};

// ============ PRINT TEMPLATE ============
const THEME_STYLES = {
  'noel': { bg: '#1a2744', band: '#243559', accent: '#0d9488', detail: '#14b8a6' },
  'saint-valentin': { bg: '#1a2744', band: '#243559', accent: '#D4A0A0', detail: '#E8A0BF' },
  'anniversaire': { bg: '#1a2744', band: '#243559', accent: '#0d9488', detail: '#14b8a6' },
  'fete-des-meres': { bg: '#1a2744', band: '#243559', accent: '#C9A0C9', detail: '#E1BEE7' },
  'fete-des-peres': { bg: '#1a2744', band: '#243559', accent: '#8EAFC4', detail: '#90CAF9' },
  'mariage': { bg: '#0d9488', band: '#0f766e', accent: '#ffffff', detail: '#D7CCC8' },
  'paques': { bg: '#1a2744', band: '#243559', accent: '#8DB580', detail: '#A8D8A8' },
  'remerciement': { bg: '#1a2744', band: '#243559', accent: '#0d9488', detail: '#14b8a6' },
};

const printBon = (bon) => {
  const t = THEME_STYLES[bon.occasion] || { bg: '#1a2744', band: '#243559', accent: '#0d9488', detail: '#0d9488' };
  const occasionLabel = OCCASIONS.find(o => o.value === bon.occasion)?.label || '';
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&display=swap" rel="stylesheet">
<style>
@page { size: A5 landscape; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { width: 210mm; height: 148mm; font-family: 'Cormorant Garamond', Georgia, serif; background: ${t.bg}; color: #fff; position: relative; overflow: hidden; }
.band { position: absolute; left: 0; top: 0; width: 68mm; height: 100%; background: ${t.band}; }
.perf { position: absolute; left: 68mm; top: 0; height: 100%; width: 2px; border-left: 2px dashed rgba(255,255,255,0.15); }
.logo-area { position: absolute; left: 8mm; top: 18mm; width: 52mm; text-align: center; }
.logo-img { width: 32mm; height: 32mm; border-radius: 50%; border: 1.5px solid ${t.accent}40; object-fit: cover; margin-bottom: 4mm; }
.logo-text { font-size: 18pt; font-weight: 700; color: ${t.accent}; letter-spacing: 4px; }
.logo-sub { font-size: 7pt; color: rgba(255,255,255,0.5); letter-spacing: 6px; margin-top: 2mm; text-transform: uppercase; }
.occasion-label { position: absolute; left: 8mm; bottom: 45mm; width: 52mm; text-align: center; font-size: 9pt; color: ${t.accent}; opacity: 0.7; letter-spacing: 3px; text-transform: uppercase; }
.content { position: absolute; left: 80mm; top: 15mm; right: 15mm; }
.title { font-size: 12pt; color: ${t.accent}; letter-spacing: 6px; text-transform: uppercase; margin-bottom: 6mm; }
.title-line { width: 40mm; height: 0.5px; background: ${t.accent}; opacity: 0.4; margin: 3mm 0 8mm; }
.montant { font-size: 48pt; font-weight: 700; color: ${t.accent}; margin: 3mm 0; }
.euro { font-size: 24pt; font-weight: 300; }
.sep { width: 100%; height: 0.5px; background: ${t.accent}; opacity: 0.3; margin: 5mm 0; }
.info { font-size: 10pt; color: rgba(255,255,255,0.7); margin: 3mm 0; line-height: 1.6; }
.info strong { color: #fff; }
.message { font-style: italic; color: ${t.detail}; font-size: 11pt; margin: 5mm 0; line-height: 1.5; }
.footer { position: absolute; bottom: 10mm; left: 80mm; right: 15mm; display: flex; justify-content: space-between; align-items: flex-end; }
.numero { font-size: 8pt; color: ${t.accent}; opacity: 0.5; letter-spacing: 2px; }
.validite { font-size: 7pt; color: rgba(255,255,255,0.35); }
.corner { position: absolute; width: 18mm; height: 18mm; }
.corner-tr { top: 8mm; right: 8mm; border-top: 0.5px solid ${t.accent}40; border-right: 0.5px solid ${t.accent}40; }
.corner-bl { bottom: 8mm; left: 72mm; border-bottom: 0.5px solid ${t.accent}40; border-left: 0.5px solid ${t.accent}40; }
</style></head><body>
<div class="band"></div>
<div class="perf"></div>
<div class="corner corner-tr"></div>
<div class="corner corner-bl"></div>
<div class="logo-area">
  <img class="logo-img" src="/images/logo-restaurant.jpg" alt="Le Mas" />
  <div class="logo-text">LE MAS</div>
  <div class="logo-sub">Restaurant · Traiteur</div>
</div>
${occasionLabel ? `<div class="occasion-label">${occasionLabel}</div>` : ''}
<div class="content">
  <div class="title">Bon Cadeau</div>
  <div class="title-line"></div>
  <div class="montant">${bon.montant}<span class="euro">&euro;</span></div>
  <div class="sep"></div>
  ${bon.beneficiaire ? `<div class="info">Pour : <strong>${bon.beneficiaire}</strong></div>` : ''}
  ${bon.client_nom ? `<div class="info">De la part de : <strong>${bon.client_nom}</strong></div>` : ''}
  ${bon.message ? `<div class="message">&laquo; ${bon.message} &raquo;</div>` : ''}
</div>
<div class="footer">
  <div class="validite">Valable jusqu'au ${bon.date_expiration}</div>
  <div class="numero">${bon.numero}</div>
</div>
</body></html>`;
  const w = window.open('', '_blank', 'width=800,height=600');
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.print(); }, 500);
};

// Download PDF via fetch
const downloadPdf = async (bon) => {
  try {
    toast.info('Génération du PDF...');
    const res = await api.get(`/bons-cadeaux/${bon.id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `bon-cadeau-${bon.numero}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('PDF téléchargé');
  } catch { toast.error('Erreur PDF'); }
};

// ============ FORM ============
const BonForm = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({ montant: '', client_nom: '', client_email: '', beneficiaire: '', message: '', occasion: '' });
  const [saving, setSaving] = useState(false);
  const [generatingMsg, setGeneratingMsg] = useState(false);

  const generateMessage = async () => {
    if (!form.occasion) { toast.error("Choisissez d'abord une occasion"); return; }
    setGeneratingMsg(true);
    try {
      const res = await api.post('/bons-cadeaux/generer-message', {
        occasion: form.occasion,
        beneficiaire: form.beneficiaire,
        client_nom: form.client_nom,
        montant: parseFloat(form.montant) || 50,
      });
      setForm(f => ({ ...f, message: res.data.message }));
      toast.success('Message genere par IA');
    } catch { toast.error('Erreur generation IA'); }
    setGeneratingMsg(false);
  };

  const submit = async () => {
    if (!form.montant || parseFloat(form.montant) <= 0) { toast.error('Montant requis'); return; }
    setSaving(true);
    try {
      const res = await api.post('/bons-cadeaux', {
        montant: parseFloat(form.montant),
        client_nom: form.client_nom,
        client_email: form.client_email,
        beneficiaire: form.beneficiaire,
        message: form.message,
        occasion: form.occasion,
      });
      if (res.data.error) { toast.error(res.data.error); setSaving(false); return; }
      toast.success(`Bon ${res.data.numero} cree — ${res.data.montant}€`);
      onCreated(res.data);
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const selectedOccasion = OCCASIONS.find(o => o.value === form.occasion);

  return (
    <div className="bg-lemas-bg/60 rounded-xl p-4 space-y-3 animate-slide-up border border-[rgb(var(--lm-gold)/0.2)]" data-testid="bon-form">
      {/* Occasion selector */}
      <div>
        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1.5">Occasion</label>
        <div className="flex flex-wrap gap-1.5" data-testid="occasion-selector">
          {OCCASIONS.map(o => (
            <button key={o.value} onClick={() => setForm(f => ({ ...f, occasion: o.value }))}
              className={`text-[10px] px-3 py-1.5 rounded-lg border transition-all ${form.occasion === o.value
                ? (OCCASION_COLORS[o.value] || 'bg-[rgb(var(--lm-gold)/0.2)] text-[rgb(var(--lm-gold))] border-[rgb(var(--lm-gold)/0.3)]')
                : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] border-transparent hover:text-[rgb(var(--lm-text))]'}`}
              data-testid={`occasion-${o.value || 'none'}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Montant</label>
          <div className="relative">
            <Input type="number" step="0.01" value={form.montant} onChange={e => setForm(f => ({ ...f, montant: e.target.value }))}
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-gold)/0.3)] font-mono text-lg pr-8" placeholder="50.00" autoFocus data-testid="bon-montant" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-gold))] font-mono">€</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Client (acheteur)</label>
          <Input value={form.client_nom} onChange={e => setForm(f => ({ ...f, client_nom: e.target.value }))}
            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" placeholder="Nom" data-testid="bon-client-nom" />
        </div>
        <div>
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Email client</label>
          <Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" placeholder="email@..." data-testid="bon-client-email" />
        </div>
        <div>
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Beneficiaire</label>
          <Input value={form.beneficiaire} onChange={e => setForm(f => ({ ...f, beneficiaire: e.target.value }))}
            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" placeholder="Qui va l'utiliser ?" data-testid="bon-beneficiaire" />
        </div>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Message personnalise</label>
            {form.occasion && (
              <button onClick={generateMessage} disabled={generatingMsg}
                className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors disabled:opacity-50"
                data-testid="generate-message-btn">
                {generatingMsg ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                {generatingMsg ? 'Generation...' : 'Generer avec IA'}
              </button>
            )}
          </div>
          <Input value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" placeholder="Bon anniversaire !" data-testid="bon-message" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} className="text-xs" data-testid="bon-cancel">Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs" data-testid="bon-submit">
          <Gift size={14} className="mr-1" /> Creer le bon
        </Button>
      </div>
    </div>
  );
};

// ============ SETTINGS PANEL ============
const SettingsPanel = ({ onClose }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/bons-cadeaux/settings').then(r => setSettings(r.data)).catch(() => toast.error('Erreur chargement parametres'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/bons-cadeaux/settings', settings);
      setSettings(res.data);
      toast.success('Parametres sauvegardes');
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  };

  const updatePkg = (idx, field, val) => {
    setSettings(s => {
      const pkgs = [...s.packages];
      pkgs[idx] = { ...pkgs[idx], [field]: field === 'amount' ? parseFloat(val) || 0 : val };
      if (field === 'amount') pkgs[idx].label = `${parseInt(val)}€`;
      return { ...s, packages: pkgs };
    });
  };

  const addPkg = () => {
    const id = `pkg_${Date.now()}`;
    setSettings(s => ({
      ...s,
      packages: [...s.packages, { id, amount: 0, label: '0€', enabled: true, order: s.packages.length }]
    }));
  };

  const removePkg = (idx) => {
    setSettings(s => ({ ...s, packages: s.packages.filter((_, i) => i !== idx) }));
  };

  const toggleOccasion = (idx) => {
    setSettings(s => {
      const occ = [...s.occasions];
      occ[idx] = { ...occ[idx], enabled: !occ[idx].enabled };
      return { ...s, occasions: occ };
    });
  };

  if (!settings) return <div className="text-center py-8 text-[rgb(var(--lm-text-muted))]"><Loader2 size={20} className="animate-spin mx-auto" /></div>;

  return (
    <div className="bg-lemas-bg/60 rounded-xl border border-[rgb(var(--lm-gold)/0.2)] p-4 space-y-5 animate-slide-up" data-testid="settings-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
          <Settings size={16} className="text-[rgb(var(--lm-gold))]" /> Parametres Boutique en Ligne
        </h3>
        <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
      </div>

      {/* En ligne toggle */}
      <div className="flex items-center justify-between bg-[rgb(var(--lm-surface))] rounded-lg p-3">
        <div>
          <p className="text-sm text-[rgb(var(--lm-text))]">Vente en ligne</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Activer/desactiver la page publique bons cadeaux</p>
        </div>
        <button onClick={() => setSettings(s => ({ ...s, en_ligne: !s.en_ligne }))} data-testid="toggle-en-ligne">
          {settings.en_ligne
            ? <ToggleRight size={28} className="text-green-400" />
            : <ToggleLeft size={28} className="text-[rgb(var(--lm-text-muted))]" />}
        </button>
      </div>

      {/* Montant libre */}
      <div className="flex items-center justify-between bg-[rgb(var(--lm-surface))] rounded-lg p-3">
        <div>
          <p className="text-sm text-[rgb(var(--lm-text))]">Montant libre</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Permettre un montant personnalise</p>
        </div>
        <button onClick={() => setSettings(s => ({ ...s, montant_libre: !s.montant_libre }))} data-testid="toggle-montant-libre">
          {settings.montant_libre
            ? <ToggleRight size={28} className="text-green-400" />
            : <ToggleLeft size={28} className="text-[rgb(var(--lm-text-muted))]" />}
        </button>
      </div>

      {settings.montant_libre && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Minimum (€)</label>
            <Input type="number" value={settings.montant_min} onChange={e => setSettings(s => ({ ...s, montant_min: parseFloat(e.target.value) || 0 }))}
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono" data-testid="montant-min" />
          </div>
          <div>
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Maximum (€)</label>
            <Input type="number" value={settings.montant_max} onChange={e => setSettings(s => ({ ...s, montant_max: parseFloat(e.target.value) || 0 }))}
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono" data-testid="montant-max" />
          </div>
        </div>
      )}

      {/* Forfaits */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Forfaits</label>
          <button onClick={addPkg} className="text-[10px] text-[rgb(var(--lm-gold))] hover:underline flex items-center gap-1" data-testid="add-package-btn">
            <Plus size={10} /> Ajouter
          </button>
        </div>
        <div className="space-y-2">
          {settings.packages.map((pkg, i) => (
            <div key={pkg.id} className="flex items-center gap-2 bg-[rgb(var(--lm-surface))] rounded-lg p-2" data-testid={`pkg-row-${i}`}>
              <button onClick={() => updatePkg(i, 'enabled', !pkg.enabled)} data-testid={`toggle-pkg-${i}`}>
                {pkg.enabled
                  ? <ToggleRight size={22} className="text-green-400" />
                  : <ToggleLeft size={22} className="text-[rgb(var(--lm-text-muted))]" />}
              </button>
              <Input type="number" value={pkg.amount} onChange={e => updatePkg(i, 'amount', e.target.value)}
                className="w-24 bg-[rgb(var(--lm-surface-elevated))] border-[rgb(var(--lm-border))] font-mono text-sm" />
              <span className="text-[rgb(var(--lm-gold))] text-sm">€</span>
              <button onClick={() => removePkg(i)} className="text-red-400/50 hover:text-red-400 ml-auto" data-testid={`remove-pkg-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Occasions */}
      <div>
        <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Occasions</label>
        <div className="flex flex-wrap gap-2">
          {settings.occasions.map((o, i) => (
            <button key={o.value} onClick={() => toggleOccasion(i)}
              className={`text-[10px] px-3 py-1.5 rounded-lg border transition-all ${
                o.enabled
                  ? 'bg-[rgb(var(--lm-gold)/0.2)] text-[rgb(var(--lm-gold))] border-[rgb(var(--lm-gold)/0.3)]'
                  : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] border-transparent line-through opacity-50'
              }`}
              data-testid={`toggle-occasion-${o.value}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onClose} className="text-xs">Fermer</Button>
        <Button onClick={save} disabled={saving} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs" data-testid="save-settings-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
          Sauvegarder
        </Button>
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
const BonsCadeaux = () => {
  const [bons, setBons] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannedBon, setScannedBon] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareLink, setShowShareLink] = useState(false);
  const [emailBonId, setEmailBonId] = useState(null);
  const [emailAddr, setEmailAddr] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const fetchBons = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatut) params.set('statut', filterStatut);
      if (search) params.set('q', search);
      const [bonsRes, statsRes] = await Promise.all([
        api.get(`/bons-cadeaux?${params}`),
        api.get('/bons-cadeaux/stats'),
      ]);
      setBons(bonsRes.data || []);
      setStats(statsRes.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [filterStatut, search]);

  useEffect(() => { fetchBons(); }, [fetchBons]);

  const handleScan = async (decodedText) => {
    setShowScanner(false);
    try {
      const res = await api.get(`/bons-cadeaux/scan/${encodeURIComponent(decodedText)}`);
      setScannedBon(res.data);
    } catch {
      toast.error('Bon non trouvé');
    }
  };

  const handleValidateScanned = async () => {
    if (!scannedBon) return;
    try {
      const res = await api.put(`/bons-cadeaux/${scannedBon.id}/valider`);
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(res.data.message);
      setScannedBon(null);
      fetchBons();
    } catch { toast.error('Erreur'); }
  };

  const scanLink = `${window.location.origin}/scan-bon`;
  const respoLink = `${window.location.origin}/responsable`;

  const copyLink = (link, label) => {
    navigator.clipboard.writeText(link);
    toast.success(`Lien ${label} copié !`);
  };

  const handleAction = async (bonId, action) => {
    try {
      const res = await api.put(`/bons-cadeaux/${bonId}/${action}`);
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(res.data.message);
      fetchBons();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (bonId) => {
    try {
      await api.delete(`/bons-cadeaux/${bonId}`);
      toast.success('Bon supprimé');
      fetchBons();
    } catch { toast.error('Erreur'); }
  };

  const handleSendEmail = async (bonId) => {
    if (!emailAddr || !emailAddr.includes('@')) { toast.error('Email invalide'); return; }
    setSendingEmail(true);
    try {
      const res = await api.post(`/bons-cadeaux/${bonId}/envoyer-email`, { email: emailAddr });
      toast.success(res.data.message || 'Email envoye !');
      setEmailBonId(null);
      setEmailAddr('');
      fetchBons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi email');
    }
    setSendingEmail(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bons-cadeaux-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[rgb(var(--lm-gold)/0.15)] flex items-center justify-center">
            <Gift size={20} className="text-[rgb(var(--lm-gold))]" />
          </div>
          <div>
            <h1 className="text-xl font-title text-[rgb(var(--lm-text))]">Bons Cadeaux</h1>
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Création, suivi et validation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowSettings(!showSettings)}
            variant="ghost" className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] text-xs"
            data-testid="settings-btn">
            <Settings size={16} className="mr-1" /> Parametres
          </Button>
          <Button onClick={() => setShowScanner(true)}
            className="bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs"
            data-testid="scan-qr-btn">
            <Camera size={16} className="mr-1" /> Scanner
          </Button>
          <Button onClick={() => setShowShareLink(!showShareLink)}
            variant="ghost" className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] text-xs"
            data-testid="share-link-btn">
            <QrCode size={16} className="mr-1" /> Liens
          </Button>
          <Button onClick={() => setShowForm(!showForm)}
            className={showForm ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90'}
            data-testid="new-bon-btn">
            {showForm ? <X size={16} /> : <><Plus size={16} className="mr-1" /> Nouveau bon</>}
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}

      {/* Share link bar */}
      {showShareLink && (
        <div className="space-y-2 animate-slide-up" data-testid="share-link-bar">
          <div className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-gold)/0.2)] rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <QrCode size={16} className="text-[rgb(var(--lm-gold))] shrink-0 hidden sm:block" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[rgb(var(--lm-gold))]">Responsable — créer + scanner</p>
              <p className="text-xs font-mono text-[rgb(var(--lm-text-muted))] truncate mt-0.5">{respoLink}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" onClick={() => copyLink(respoLink, 'responsable')} className="bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.25)] text-xs" data-testid="copy-respo-link">
                <Copy size={12} className="mr-1" /> Copier
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.open(respoLink, '_blank')} className="text-[rgb(var(--lm-text-muted))] text-xs" data-testid="open-respo-link">
                <ExternalLink size={12} />
              </Button>
            </div>
          </div>
          <div className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border)/0.5)] rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <Camera size={16} className="text-green-400 shrink-0 hidden sm:block" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-green-400">Serveuses — scanner uniquement</p>
              <p className="text-xs font-mono text-[rgb(var(--lm-text-muted))] truncate mt-0.5">{scanLink}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" onClick={() => copyLink(scanLink, 'serveuses')} className="bg-green-500/15 text-green-400 hover:bg-green-500/25 text-xs" data-testid="copy-scan-link">
                <Copy size={12} className="mr-1" /> Copier
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.open(scanLink, '_blank')} className="text-[rgb(var(--lm-text-muted))] text-xs" data-testid="open-scan-link">
                <ExternalLink size={12} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bons-stats">
          <div className="kpi-card text-center">
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">En attente</p>
            <p className="font-mono text-xl text-orange-400 mt-1">{formatCurrency(stats.total_attente + stats.total_cree)}</p>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{stats.nb_actifs} bon{stats.nb_actifs > 1 ? 's' : ''}</p>
          </div>
          <div className="kpi-card text-center">
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Utilisés</p>
            <p className="font-mono text-xl text-green-400 mt-1">{formatCurrency(stats.total_utilise)}</p>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{stats.nb_utilises} bon{stats.nb_utilises > 1 ? 's' : ''}</p>
          </div>
          <div className="kpi-card text-center">
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Expirés</p>
            <p className="font-mono text-xl text-red-400 mt-1">{stats.nb_expires}</p>
          </div>
          <div className="kpi-card text-center border-[rgb(var(--lm-gold)/0.2)]">
            <p className="text-[10px] text-[rgb(var(--lm-gold))] uppercase tracking-wider">Total</p>
            <p className="font-mono text-xl text-[rgb(var(--lm-gold))] mt-1">{stats.nb_total}</p>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <BonForm
          onCreated={(bon) => { setShowForm(false); fetchBons(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-2" data-testid="bons-filters">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par n°, nom, bénéficiaire..."
            className="pl-9 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
            data-testid="bons-search" />
        </div>
        <div className="flex gap-1.5">
          {['', 'CREE', 'EN_ATTENTE', 'UTILISE', 'EXPIRE'].map(s => (
            <button key={s} onClick={() => setFilterStatut(s)}
              className={`text-[10px] px-2.5 py-1.5 rounded-lg transition-colors ${filterStatut === s ? 'bg-[rgb(var(--lm-gold)/0.2)] text-[rgb(var(--lm-gold))]' : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`}
              data-testid={`filter-${s || 'all'}`}>
              {s ? (STATUT_COLORS[s]?.label || s) : 'Tous'}
            </button>
          ))}
        </div>
      </div>

      {/* Bons list */}
      <div className="space-y-2" data-testid="bons-list">
        {loading ? (
          <div className="text-center py-8 text-[rgb(var(--lm-text-muted))] text-sm">Chargement...</div>
        ) : bons.length === 0 ? (
          <div className="text-center py-8">
            <Gift size={32} className="mx-auto mb-2 text-[rgb(var(--lm-text-muted))]" />
            <p className="text-sm text-[rgb(var(--lm-text-muted))]">{search ? 'Aucun bon trouvé' : 'Aucun bon cadeau créé'}</p>
          </div>
        ) : bons.map(bon => (
          <div key={bon.id} className="kpi-card overflow-hidden" data-testid={`bon-${bon.id}`}>
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(expanded === bon.id ? null : bon.id)}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-lg bg-[rgb(var(--lm-gold)/0.1)] flex items-center justify-center shrink-0">
                  <Gift size={14} className="text-[rgb(var(--lm-gold))]" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{bon.numero}</span>
                    <StatutBadge statut={bon.statut} />
                    {bon.paye_en_ligne && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20" data-testid={`online-badge-${bon.id}`}>
                        Achat en ligne
                      </span>
                    )}
                    {bon.occasion && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${OCCASION_COLORS[bon.occasion] || 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] border-[rgb(var(--lm-border))]'}`} data-testid={`occasion-tag-${bon.id}`}>
                        {OCCASIONS.find(o => o.value === bon.occasion)?.label || bon.occasion}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {bon.beneficiaire && <span className="font-medium text-[rgb(var(--lm-text))] truncate">{bon.beneficiaire}</span>}
                    {bon.client_nom && <span className="text-[rgb(var(--lm-text-muted))] text-xs">de {bon.client_nom}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-2">
                <span className="font-mono text-lg text-[rgb(var(--lm-gold))] font-semibold">{bon.montant}€</span>
                <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{bon.date_creation}</span>
                {expanded === bon.id ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
              </div>
            </div>

            {expanded === bon.id && (
              <div className="mt-3 pt-3 border-t border-[rgb(var(--lm-border)/0.5)] space-y-2 animate-slide-up">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div><span className="text-[rgb(var(--lm-text-muted))]">Créé le</span><br /><span className="font-mono">{bon.date_creation}</span></div>
                  <div><span className="text-[rgb(var(--lm-text-muted))]">Expire le</span><br /><span className="font-mono">{bon.date_expiration}</span></div>
                  {bon.date_utilisation && <div><span className="text-[rgb(var(--lm-text-muted))]">Utilisé le</span><br /><span className="font-mono text-green-400">{bon.date_utilisation}</span></div>}
                  {bon.client_email && <div><span className="text-[rgb(var(--lm-text-muted))]">Email</span><br /><span>{bon.client_email}</span></div>}
                  {bon.paye_en_ligne && <div><span className="text-[rgb(var(--lm-text-muted))]">Paiement</span><br /><span className="text-blue-400">Stripe en ligne</span></div>}
                </div>
                {bon.message && <p className="text-xs italic text-[rgb(var(--lm-gold))]">"{bon.message}"</p>}

                <div className="flex flex-wrap gap-2 pt-1">
                  {bon.statut === 'CREE' && (
                    <Button size="sm" onClick={() => handleAction(bon.id, 'remettre')} className="bg-orange-400/20 text-orange-400 hover:bg-orange-400/30 text-xs" data-testid={`remettre-${bon.id}`}>
                      <SendIcon size={12} className="mr-1" /> Remis au client
                    </Button>
                  )}
                  {(bon.statut === 'CREE' || bon.statut === 'EN_ATTENTE') && (
                    <Button size="sm" onClick={() => handleAction(bon.id, 'valider')} className="bg-green-500 text-white hover:bg-green-600 text-xs" data-testid={`valider-${bon.id}`}>
                      <Check size={12} className="mr-1" /> Valider et encaisser
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => printBon(bon)} className="text-[rgb(var(--lm-gold))] text-xs" data-testid={`print-${bon.id}`}>
                    <Printer size={12} className="mr-1" /> Imprimer
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(bon)} className="text-[rgb(var(--lm-gold))] text-xs" data-testid={`pdf-${bon.id}`}>
                    <Gift size={12} className="mr-1" /> Télécharger PDF
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => { setEmailBonId(emailBonId === bon.id ? null : bon.id); setEmailAddr(bon.client_email || ''); }}
                    className="text-blue-400 text-xs" data-testid={`email-${bon.id}`}>
                    <Mail size={12} className="mr-1" /> {bon.email_envoye ? 'Renvoyer' : 'Envoyer'} par email
                  </Button>
                  {bon.statut !== 'UTILISE' && bon.statut !== 'EXPIRE' && (
                    <Button size="sm" variant="ghost" onClick={() => handleAction(bon.id, 'expirer')} className="text-red-400/60 hover:text-red-400 text-xs" data-testid={`expirer-${bon.id}`}>
                      <Ban size={12} className="mr-1" /> Expirer
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(bon.id)} className="text-red-400/40 hover:text-red-400 text-xs" data-testid={`delete-${bon.id}`}>
                    <Trash2 size={12} className="mr-1" /> Supprimer
                  </Button>
                </div>

                {emailBonId === bon.id && (
                  <div className="flex items-center gap-2 pt-2 animate-slide-up" data-testid={`email-form-${bon.id}`}>
                    <Mail size={14} className="text-blue-400 shrink-0" />
                    <Input type="email" value={emailAddr} onChange={e => setEmailAddr(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendEmail(bon.id)}
                      placeholder="email@destinataire.com"
                      className="flex-1 bg-[rgb(var(--lm-surface))] border-blue-400/30 text-sm h-8" autoFocus data-testid={`email-input-${bon.id}`} />
                    <Button size="sm" onClick={() => handleSendEmail(bon.id)} disabled={sendingEmail}
                      className="bg-blue-500 text-white hover:bg-blue-600 text-xs h-8 px-3" data-testid={`email-send-${bon.id}`}>
                      {sendingEmail ? <Loader2 size={12} className="animate-spin" /> : 'Envoyer'}
                    </Button>
                    <button onClick={() => setEmailBonId(null)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid={`email-cancel-${bon.id}`}>
                      <X size={14} />
                    </button>
                  </div>
                )}

                {bon.email_envoye && !emailBonId && (
                  <p className="text-[10px] text-blue-400/50 pt-1">
                    Envoye a {bon.email_destinataire} le {bon.email_date?.slice(0, 10)}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* QR Scanner Modal */}
      <QrScannerModal
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleScan}
      />

      {/* Scanned Bon Modal */}
      {scannedBon && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="scanned-bon-modal">
          <div className="bg-[rgb(var(--lm-surface))] rounded-2xl w-full max-w-sm border border-[rgb(var(--lm-gold)/0.3)] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--lm-border)/0.5)]">
              <h3 className="text-sm font-medium text-[rgb(var(--lm-text))] flex items-center gap-2">
                <Gift size={16} className="text-[rgb(var(--lm-gold))]" /> Bon scanné
              </h3>
              <button onClick={() => setScannedBon(null)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="close-scanned-modal">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 text-center">
              <p className="text-4xl font-bold text-[rgb(var(--lm-gold))] font-mono">{scannedBon.montant}€</p>
              <p className="text-xs font-mono text-[rgb(var(--lm-text-muted))] mt-1">{scannedBon.numero}</p>
              <div className="mt-3">
                <StatutBadge statut={scannedBon.statut} />
              </div>
              {scannedBon.beneficiaire && <p className="text-sm text-[rgb(var(--lm-text))] mt-3">Pour : <strong>{scannedBon.beneficiaire}</strong></p>}
              {scannedBon.client_nom && <p className="text-xs text-[rgb(var(--lm-text-muted))]">De : {scannedBon.client_nom}</p>}
              <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-2">Expire le {scannedBon.date_expiration}</p>
            </div>
            <div className="px-4 pb-4 space-y-2">
              {(scannedBon.statut === 'CREE' || scannedBon.statut === 'EN_ATTENTE') && (
                <Button onClick={handleValidateScanned} className="w-full bg-green-500 text-white hover:bg-green-600" data-testid="validate-scanned-btn">
                  <Check size={16} className="mr-2" /> Valider et encaisser
                </Button>
              )}
              {scannedBon.statut === 'UTILISE' && (
                <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-orange-400/10 text-orange-400 text-xs">
                  <Clock size={14} /> Déjà utilisé{scannedBon.date_utilisation ? ` le ${scannedBon.date_utilisation}` : ''}
                </div>
              )}
              {scannedBon.statut === 'EXPIRE' && (
                <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-red-400/10 text-red-400 text-xs">
                  <Ban size={14} /> Ce bon est expiré
                </div>
              )}
              <Button variant="ghost" onClick={() => setScannedBon(null)} className="w-full text-[rgb(var(--lm-text-muted))] text-xs" data-testid="close-result-btn">Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BonsCadeaux;
